/**
 * Saved Creator Lists — Brand-only persistent folders of creators.
 *
 * Two views:
 *   /brand/saved-lists                → grid of list cards
 *   /brand/saved-lists/:id            → open a single list (creator cards)
 *
 * A "Start Campaign" button on the detail view carries the selected creators
 * into the campaign submission flow (via BrandDiscover ?prefillFrom= query).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bookmark, Plus, Trash2, Users, Loader2, ChevronLeft, Sparkles,
  IndianRupee, Instagram, MapPin, Edit2, Check, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sidebar } from '../../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtNum = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(1)}Cr`;
  if (v >= 100_000) return `${(v / 100_000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString('en-IN');
};

// ---------------------------------------------------------------------------
// Grid view — all saved lists for this brand.
// ---------------------------------------------------------------------------
export const BrandSavedLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/brand/saved-lists`, { withCredentials: true });
      setLists(data || []);
    } catch (e) {
      toast.error('Failed to load saved lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const nm = newName.trim();
    if (!nm) return;
    try {
      setCreating(true);
      await axios.post(`${API_URL}/api/brand/saved-lists`, { name: nm }, { withCredentials: true });
      setNewName('');
      setShowNewForm(false);
      await load();
      toast.success(`Created "${nm}"`);
    } catch {
      toast.error('Could not create list');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its saved creators?`)) return;
    try {
      await axios.delete(`${API_URL}/api/brand/saved-lists/${id}`, { withCredentials: true });
      await load();
      toast.success('List deleted');
    } catch {
      toast.error('Could not delete');
    }
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 p-8 text-[#0F0F0F] dark:text-white" data-testid="saved-lists-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#0F0F0F] dark:text-white flex items-center gap-2">
              <Bookmark size={26} className="text-[#FF451A]" /> Saved Lists
            </h1>
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-1">
              Persistent folders of creators. Great for keeping favourites, longlists, or theme-based rosters ready for the next campaign.
            </p>
          </div>
          <Button
            onClick={() => setShowNewForm((v) => !v)}
            className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 py-2 flex items-center gap-1.5"
            data-testid="new-saved-list-button"
          >
            <Plus size={14} /> New list
          </Button>
        </div>

        {showNewForm && (
          <div className="mb-6 p-4 rounded-2xl border border-black/[0.08] dark:border-white/10 bg-[#F7F7F8] dark:bg-[#0F1011] flex items-center gap-2" data-testid="new-list-form">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder="Give your list a name..."
              className="rounded-lg text-sm bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              autoFocus
              data-testid="new-list-name-input"
            />
            <Button
              onClick={create}
              disabled={creating || !newName.trim()}
              className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 disabled:opacity-40"
              data-testid="new-list-create-button"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : 'Create'}
            </Button>
            <button
              type="button"
              onClick={() => { setShowNewForm(false); setNewName(''); }}
              className="text-[#6B6B6B] dark:text-zinc-400 hover:text-[#0F0F0F] dark:hover:text-white text-sm px-3"
              data-testid="new-list-cancel-button"
            >
              Cancel
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-[#6B6B6B] dark:text-zinc-400 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : lists.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-black/[0.10] dark:border-white/10 rounded-2xl" data-testid="saved-lists-empty">
            <Bookmark size={40} className="mx-auto text-[#FF451A]/60 mb-3" />
            <div className="text-[#0F0F0F] dark:text-white font-semibold">No saved lists yet</div>
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-1">Start bookmarking creators from Discover to build your first folder.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {lists.map((l) => (
              <Link
                key={l.list_id}
                to={`/brand/saved-lists/${l.list_id}`}
                className="group bg-white dark:bg-[#0F1011] border border-black/[0.08] dark:border-white/10 rounded-2xl p-5 hover:border-[#FF451A]/40 transition-colors relative"
                data-testid={`saved-list-card-${l.list_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A]">
                    <Bookmark size={18} />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(l.list_id, l.name); }}
                    className="opacity-0 group-hover:opacity-100 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-all"
                    data-testid={`delete-list-${l.list_id}`}
                    title="Delete list"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="font-semibold text-[#0F0F0F] dark:text-white text-base truncate">{l.name}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-[#6B6B6B] dark:text-zinc-400">
                  <span className="flex items-center gap-1"><Users size={12} /> {l.creator_count}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider">
                    {l.updated_at ? new Date(l.updated_at).toLocaleDateString() : ''}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail view — creators inside a specific saved list.
// ---------------------------------------------------------------------------
export const BrandSavedListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/brand/saved-lists`, { withCredentials: true });
      const found = (data || []).find((l) => l.list_id === id);
      if (!found) {
        toast.error('List not found');
        navigate('/brand/saved-lists');
        return;
      }
      setList(found);
      setRenameValue(found.name);
      // Batch-fetch creator profiles.  Each is only ~1KB so this is fine at
      // small list sizes; can be replaced with a bulk endpoint later.
      const cs = await Promise.all(
        (found.creator_ids || []).map((cid) =>
          axios.get(`${API_URL}/api/creators/${cid}`, { withCredentials: true })
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      setCreators(cs.filter(Boolean));
    } catch {
      toast.error('Failed to load list');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const removeCreator = async (cid) => {
    try {
      await axios.delete(`${API_URL}/api/brand/saved-lists/${id}/creators/${cid}`, { withCredentials: true });
      setCreators((cs) => cs.filter((c) => c.id !== cid));
      toast.success('Creator removed');
    } catch {
      toast.error('Could not remove');
    }
  };

  const saveRename = async () => {
    const nm = renameValue.trim();
    if (!nm || nm === list.name) { setRenaming(false); return; }
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/brand/saved-lists/${id}`,
        { name: nm },
        { withCredentials: true }
      );
      setList(data);
      setRenaming(false);
      toast.success('Renamed');
    } catch {
      toast.error('Could not rename');
    }
  };

  const startCampaign = () => {
    // Carry the creator ids into the discover flow as a query param.
    // BrandDiscover reads ?prefillFrom= to seed its shortlist auto-open flow.
    const ids = creators.map((c) => c.id).join(',');
    navigate(`/brand/discover?prefillFrom=${encodeURIComponent(ids)}`);
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 p-8 text-[#0F0F0F] dark:text-white" data-testid="saved-list-detail">
      <div className="max-w-6xl mx-auto">
        <Link to="/brand/saved-lists" className="inline-flex items-center gap-1 text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] mb-4">
          <ChevronLeft size={14} /> All saved lists
        </Link>

        <div className="flex items-start justify-between mb-6">
          <div className="flex-1 min-w-0">
            {renaming ? (
              <div className="flex items-center gap-2" data-testid="rename-form">
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') { setRenaming(false); setRenameValue(list.name); } }}
                  className="max-w-md rounded-lg text-2xl font-bold bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  autoFocus
                  data-testid="rename-input"
                />
                <Button onClick={saveRename} className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-3" data-testid="rename-save-button">
                  <Check size={14} />
                </Button>
                <Button onClick={() => { setRenaming(false); setRenameValue(list?.name || ''); }} className="border border-black/[0.10] dark:border-white/10 bg-transparent hover:bg-white/5 rounded-full px-3">
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold text-[#0F0F0F] dark:text-white truncate">{list?.name || ''}</h1>
                <button
                  type="button"
                  onClick={() => setRenaming(true)}
                  className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF451A]"
                  data-testid="rename-button"
                  title="Rename"
                >
                  <Edit2 size={14} />
                </button>
              </div>
            )}
            <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-1">
              {creators.length} creator{creators.length === 1 ? '' : 's'}
            </div>
          </div>
          <Button
            onClick={startCampaign}
            disabled={!creators.length}
            className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 py-2 flex items-center gap-1.5 disabled:opacity-40"
            data-testid="start-campaign-button"
          >
            <Sparkles size={14} /> Start Campaign
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#6B6B6B] dark:text-zinc-400 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading creators…
          </div>
        ) : creators.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-black/[0.10] dark:border-white/10 rounded-2xl" data-testid="saved-list-detail-empty">
            <Users size={40} className="mx-auto text-[#FF451A]/60 mb-3" />
            <div className="text-[#0F0F0F] dark:text-white font-semibold">No creators saved here yet</div>
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-1">Open Discover, hit the bookmark icon on any creator and pick this list.</p>
            <Link to="/brand/discover" className="inline-block mt-4 text-sm font-medium text-[#FF451A] hover:underline">Browse creators →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {creators.map((c) => (
              <div
                key={c.id}
                className="bg-white dark:bg-[#0F1011] border border-black/[0.08] dark:border-white/10 rounded-2xl p-4 relative"
                data-testid={`saved-creator-card-${c.id}`}
              >
                <button
                  type="button"
                  onClick={() => removeCreator(c.id)}
                  className="absolute top-3 right-3 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30]"
                  data-testid={`remove-creator-${c.id}`}
                  title="Remove from list"
                >
                  <Trash2 size={14} />
                </button>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white font-bold shrink-0">
                    {c.name?.[0]?.toUpperCase() || 'C'}
                  </div>
                  <div className="flex-1 min-w-0 pr-6">
                    <div className="font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                    {c.instagram_handle && (
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 flex items-center gap-1 truncate">
                        <Instagram size={11} /> @{c.instagram_handle}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(c.categories || []).slice(0, 2).map((cat) => (
                    <span key={cat} className="px-2 py-0.5 text-[10px] rounded-full bg-[#FF451A]/10 text-[#FF451A] font-medium">
                      {cat}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1 text-[#6B6B6B] dark:text-zinc-400">
                    <Users size={10} /> {fmtNum(c.instagram_followers)}
                  </div>
                  {c.cities?.[0] && (
                    <div className="flex items-center gap-1 text-[#6B6B6B] dark:text-zinc-400 truncate">
                      <MapPin size={10} /> {c.cities[0]}
                    </div>
                  )}
                  {c.pricing_reel != null && (
                    <div className="flex items-center gap-1 text-[#6B6B6B] dark:text-zinc-400 col-span-2">
                      <IndianRupee size={10} /> {fmtNum(c.pricing_reel)} / reel
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default BrandSavedLists;
